import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../../services/supabaseClient';

/*
  LP de captação de leads — destino do QR Code IMPRESSO (rota /qr1).
  Co-branded MF Real Estate + RSF Empreendimentos. Foco: lançamentos e
  pré-lançamentos (Alphaville). Funil multi-step que engaja com perguntas
  de clique antes de pedir o contato (converte melhor).

  Persistência: insert anon em mf_leads_site (origem 'qr1-lancamentos') +
  qualificação no JSONB, depois dispara /api/mf-lead → notificar-lead-site-mf.

  ⚠️ Logo RSF: placeholder textual até o arquivo chegar (Nicolas).
     Trocar <RsfWordmark/> por <img src="/mf-real-state/logos/rsf-...png"/>.
  ⚠️ Design: base alto-padrão; refinar quando a referência do João chegar.
*/

const NAVY = '#0F1828';
const NAVY_2 = '#16243a';
const GOLD = '#C9A06A';
const TEXT_MUTED = '#A9B3C2';

const TIPOS_RENDA = [
  { id: 'short_stay', label: 'Short stay', desc: 'Aluguel por temporada' },
  { id: 'long_stay', label: 'Long stay', desc: 'Locação de longo prazo' },
  { id: 'flipping', label: 'Flipping', desc: 'Comprar, valorizar e vender' },
  { id: 'leilao', label: 'Leilão', desc: 'Arremate com desconto' },
  { id: 'incorp_casa', label: 'Incorporação — casas', desc: 'Construir e vender casas' },
  { id: 'incorp_predio', label: 'Incorporação — prédios', desc: 'Empreender verticais' },
];

const formatPhone = (value) => {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

// Token do cliente MF Real Estate no Bianca (Conversas). Cloud API oficial.
const MF_TOKEN = 'QOQMRWtDQA9W8pluLNSV';
const ALPHA_LABELS = { invisto: 'Já invisto', interesse: 'Tenho interesse', conhecer: 'Quero conhecer' };
const RENDA_LABELS = Object.fromEntries(TIPOS_RENDA.map((t) => [t.id, t.label]));
// formatFormResponse no LeadInfoPanel troca '_' por espaço e capitaliza cada palavra.
// Então trocamos espaços por '_' e tiramos vírgulas (que separam tags) dos valores.
const u = (s) => String(s ?? '').trim().replace(/,/g, ' ').replace(/\s+/g, '_') || '—';

// ---- Logos -----------------------------------------------------------------
// rsf-white.png: logo RSF recolorida pra branco (negativa) p/ contraste no navy.
const CoBrand = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', marginBottom: '40px' }}>
    <img src="/mf-real-state/logos/mf-logo-white-clean.png" alt="MF Real Estate" style={{ height: '38px' }} />
    <div style={{ width: '1px', height: '32px', background: 'rgba(255,255,255,0.18)' }} />
    <img src="/mf-real-state/logos/rsf-white.png" alt="RSF Empreendimentos" style={{ height: '26px' }} />
  </div>
);

// ---- UI primitives ---------------------------------------------------------
const ChoiceCard = ({ selected, onClick, title, desc, multi }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: '100%',
      textAlign: 'left',
      padding: '18px 20px',
      borderRadius: '14px',
      border: `1.5px solid ${selected ? GOLD : 'rgba(255,255,255,0.14)'}`,
      background: selected ? 'rgba(201,160,106,0.12)' : 'rgba(255,255,255,0.03)',
      color: '#fff',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      transition: 'all 0.18s ease',
    }}
  >
    <span>
      <span style={{ fontSize: '16px', fontWeight: 600, display: 'block' }}>{title}</span>
      {desc && <span style={{ fontSize: '13px', color: TEXT_MUTED }}>{desc}</span>}
    </span>
    <span
      style={{
        flexShrink: 0,
        width: '22px',
        height: '22px',
        borderRadius: multi ? '6px' : '50%',
        border: `1.5px solid ${selected ? GOLD : 'rgba(255,255,255,0.3)'}`,
        background: selected ? GOLD : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {selected && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={NAVY} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </span>
  </button>
);

const StepTitle = ({ children, sub }) => (
  <>
    <h2 style={{ fontSize: 'clamp(22px, 5vw, 30px)', fontWeight: 600, margin: '0 0 8px', lineHeight: 1.2 }}>{children}</h2>
    {sub && <p style={{ fontSize: '15px', color: TEXT_MUTED, margin: '0 0 28px' }}>{sub}</p>}
  </>
);

const PrimaryButton = ({ children, disabled, onClick, type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    style={{
      width: '100%',
      padding: '16px',
      borderRadius: '999px',
      border: 'none',
      background: disabled ? 'rgba(201,160,106,0.4)' : GOLD,
      color: NAVY,
      fontSize: '16px',
      fontWeight: 700,
      letterSpacing: '0.3px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      marginTop: '8px',
    }}
  >
    {children}
  </button>
);

const Field = ({ label, ...props }) => (
  <label style={{ display: 'block', marginBottom: '16px', textAlign: 'left' }}>
    <span style={{ fontSize: '13px', color: TEXT_MUTED, display: 'block', marginBottom: '6px' }}>{label}</span>
    <input
      {...props}
      style={{
        width: '100%',
        padding: '14px 16px',
        borderRadius: '12px',
        border: '1.5px solid rgba(255,255,255,0.14)',
        background: 'rgba(255,255,255,0.04)',
        color: '#fff',
        fontSize: '16px',
        outline: 'none',
        boxSizing: 'border-box',
      }}
    />
  </label>
);

// ---- Main ------------------------------------------------------------------
const TOTAL_STEPS = 5; // intro(0) não conta na barra; perguntas 1..4 + envio

const QrLancamentosLP = () => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    nome: '', profissao: '', cidade: '', telefone: '', email: '',
    investeImoveis: null,      // true | false
    tiposRenda: [],            // string[]
    alphaville: null,          // 'invisto' | 'interesse' | 'conhecer'
  });

  const set = (patch) => setForm((p) => ({ ...p, ...patch }));
  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  const toggleRenda = (id) =>
    set({ tiposRenda: form.tiposRenda.includes(id) ? form.tiposRenda.filter((r) => r !== id) : [...form.tiposRenda, id] });

  const contatoValido =
    form.nome.trim() &&
    form.cidade.trim() &&
    form.telefone.replace(/\D/g, '').length >= 10 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const handleSubmit = async () => {
    if (!contatoValido || loading) return;
    setLoading(true);
    const telefoneDigits = form.telefone.replace(/\D/g, '');
    const qualificacao = {
      profissao: form.profissao.trim() || null,
      investe_imoveis: form.investeImoveis,
      tipos_renda: form.tiposRenda,
      alphaville: form.alphaville,
    };

    // 1) Registro no site MF (histórico).
    await supabase
      .from('mf_leads_site')
      .insert({
        nome: form.nome.trim(),
        telefone: telefoneDigits,
        email: form.email.trim(),
        cidade: form.cidade.trim(),
        origem: 'qr1-lancamentos',
        qualificacao,
      });

    // 2) Lead no Bianca (aparece em Conversas), com a Bianca DESATIVADA
    //    (atividade='desativado') — ela não atende esse lead. respostas_forms
    //    no formato que o LeadInfoPanel renderiza (CSV "Label:_valor").
    const tel55 = telefoneDigits.startsWith('55') ? telefoneDigits : `55${telefoneDigits}`;
    const respostasForms = [
      `Profissão:_${u(form.profissao)}`,
      `Já_investe_em_imóveis:_${form.investeImoveis ? 'Sim' : 'Não'}`,
      `Retorno_buscado:_${form.tiposRenda.map((id) => u(RENDA_LABELS[id])).join('_/_') || '—'}`,
      `Alphaville:_${u(ALPHA_LABELS[form.alphaville])}`,
      `Cidade:_${u(form.cidade)}`,
    ].join(', ');

    // Conversas esconde leads sem nenhuma mensagem (filtra ultima_mensagem/
    // mensagens/transcricao not null). Preenche ultima_mensagem com um resumo
    // do cadastro pra o lead aparecer na lista (e dar preview útil ao corretor).
    const resumoMsg =
      `Cadastro via QR Code (Lançamentos) · Profissão: ${form.profissao.trim() || '—'}` +
      ` · Investe: ${form.investeImoveis ? 'Sim' : 'Não'}` +
      ` · Retorno: ${form.tiposRenda.map((id) => RENDA_LABELS[id]).join(', ') || '—'}` +
      ` · Alphaville: ${ALPHA_LABELS[form.alphaville] || '—'} · Cidade: ${form.cidade.trim() || '—'}`;
    const nowIso = new Date().toISOString();

    try {
      await supabase.from('bianca_leads').insert({
        lead_id: crypto.randomUUID(),
        token: MF_TOKEN,
        nome: form.nome.trim(),
        telefone: tel55,
        email: form.email.trim(),
        produto: 'QR Code - Lançamentos',
        origem: 'qr1-lancamentos',
        status: 'novo',
        atividade: 'desativado',
        api_type: 'oficial',
        respostas_forms: respostasForms,
        qualificacao: JSON.stringify(qualificacao),
        created_at: nowIso,
        // ordena por ultima_mensagem_data_hora (nulls no fim) -> topo da lista
        ultima_mensagem_data_hora: nowIso,
        // necessário pra passar no filtro "tem mensagem" da lista de Conversas
        ultima_mensagem: resumoMsg,
      });
    } catch { /* não bloqueia o usuário: o registro do site já foi salvo */ }

    setLoading(false);
    setDone(true);
  };

  const progress = step === 0 || done ? 0 : (step / TOTAL_STEPS) * 100;

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        background: `radial-gradient(circle at 50% 18%, ${NAVY_2} 0%, ${NAVY} 62%)`,
        color: '#fff',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '32px 20px 48px',
        boxSizing: 'border-box',
      }}
    >
      {/* Barra de progresso */}
      {step > 0 && !done && (
        <div style={{ width: '100%', maxWidth: '440px', marginBottom: '28px' }}>
          <div style={{ height: '4px', borderRadius: '999px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
            <motion.div
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
              style={{ height: '100%', background: GOLD }}
            />
          </div>
          <button
            type="button"
            onClick={back}
            style={{ marginTop: '12px', background: 'none', border: 'none', color: TEXT_MUTED, fontSize: '13px', cursor: 'pointer', padding: 0 }}
          >
            ← Voltar
          </button>
        </div>
      )}

      <div style={{ width: '100%', maxWidth: '440px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <AnimatePresence mode="wait">
          {/* ---------------- DONE ---------------- */}
          {done ? (
            <motion.div key="done" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: 'center' }}>
              <CoBrand />
              <div style={{ width: '64px', height: '64px', margin: '0 auto 24px', borderRadius: '50%', background: 'rgba(201,160,106,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2 style={{ fontSize: '26px', fontWeight: 600, margin: '0 0 12px' }}>Cadastro confirmado</h2>
              <p style={{ fontSize: '15px', color: TEXT_MUTED, lineHeight: 1.6, maxWidth: '360px', margin: '0 auto' }}>
                Você vai receber em primeira mão os lançamentos e pré-lançamentos selecionados pra você. Em breve nossos especialistas entram em contato.
              </p>
            </motion.div>
          ) : step === 0 ? (
            /* ---------------- INTRO ---------------- */
            <motion.div key="intro" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} style={{ textAlign: 'center' }}>
              <CoBrand />
              <div style={{ fontSize: '12px', letterSpacing: '3px', textTransform: 'uppercase', color: GOLD, marginBottom: '16px' }}>
                Lançamentos & Pré-lançamentos
              </div>
              <h1 style={{ fontSize: 'clamp(28px, 7vw, 40px)', fontWeight: 600, margin: '0 0 16px', lineHeight: 1.15 }}>
                As melhores oportunidades, antes de todo mundo
              </h1>
              <p style={{ fontSize: '16px', color: TEXT_MUTED, lineHeight: 1.6, margin: '0 0 36px' }}>
                Quer saber dos lançamentos e ficar atento aos próximos pré-lançamentos? Cadastre-se agora e receba em primeira mão.
              </p>
              <PrimaryButton onClick={next}>Quero saber dos lançamentos</PrimaryButton>
            </motion.div>
          ) : step === 1 ? (
            /* ---------------- Q1: investe? ---------------- */
            <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
              <StepTitle sub="Pra gente selecionar as oportunidades certas pra você.">Você já investe em imóveis?</StepTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <ChoiceCard title="Sim, já invisto" selected={form.investeImoveis === true} onClick={() => { set({ investeImoveis: true }); next(); }} />
                <ChoiceCard title="Ainda não, quero começar" selected={form.investeImoveis === false} onClick={() => { set({ investeImoveis: false }); next(); }} />
              </div>
            </motion.div>
          ) : step === 2 ? (
            /* ---------------- Q2: tipos de renda ---------------- */
            <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
              <StepTitle sub="Pode escolher mais de uma.">Que tipo de retorno te interessa?</StepTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {TIPOS_RENDA.map((t) => (
                  <ChoiceCard key={t.id} multi title={t.label} desc={t.desc} selected={form.tiposRenda.includes(t.id)} onClick={() => toggleRenda(t.id)} />
                ))}
              </div>
              <PrimaryButton onClick={next} disabled={form.tiposRenda.length === 0}>Continuar</PrimaryButton>
            </motion.div>
          ) : step === 3 ? (
            /* ---------------- Q3: Alphaville ---------------- */
            <motion.div key="s3" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
              <StepTitle sub="Um dos mercados mais valorizados da região.">E sobre Alphaville?</StepTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <ChoiceCard title="Já invisto em Alphaville" selected={form.alphaville === 'invisto'} onClick={() => { set({ alphaville: 'invisto' }); next(); }} />
                <ChoiceCard title="Tenho interesse em investir" selected={form.alphaville === 'interesse'} onClick={() => { set({ alphaville: 'interesse' }); next(); }} />
                <ChoiceCard title="Quero conhecer melhor" selected={form.alphaville === 'conhecer'} onClick={() => { set({ alphaville: 'conhecer' }); next(); }} />
              </div>
            </motion.div>
          ) : (
            /* ---------------- Q4: contato ---------------- */
            <motion.div key="s4" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}>
              <StepTitle sub="Falta pouco. Onde podemos te enviar as oportunidades?">Seus dados</StepTitle>
              <Field label="Nome completo" type="text" autoComplete="name" placeholder="Seu nome" value={form.nome} onChange={(e) => set({ nome: e.target.value })} />
              <Field label="Profissão" type="text" autoComplete="organization-title" placeholder="Sua profissão" value={form.profissao} onChange={(e) => set({ profissao: e.target.value })} />
              <Field label="Cidade" type="text" autoComplete="address-level2" placeholder="Onde você mora" value={form.cidade} onChange={(e) => set({ cidade: e.target.value })} />
              <Field label="Celular / WhatsApp" type="tel" autoComplete="tel" placeholder="(11) 99999-9999" value={form.telefone} onChange={(e) => set({ telefone: formatPhone(e.target.value) })} />
              <Field label="E-mail" type="email" autoComplete="email" placeholder="seu@email.com" value={form.email} onChange={(e) => set({ email: e.target.value })} />
              <PrimaryButton onClick={handleSubmit} disabled={!contatoValido || loading}>
                {loading ? 'Enviando...' : 'Quero receber os lançamentos'}
              </PrimaryButton>
              <p style={{ fontSize: '12px', color: TEXT_MUTED, textAlign: 'center', marginTop: '14px' }}>
                Seus dados estão seguros e não serão compartilhados.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default QrLancamentosLP;
